import { useMemo, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { ArrowRight, GitCompareArrows, ShieldOff, X } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SEND_GATE_SETTINGS_URL } from '@/constants/paths';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { useDeviationFilterOptions, useDeviations, useSendGateConfig } from '@/hooks/use-deviations';
import { usePermissions } from '@/hooks/use-permissions';
import { canManageSentinelMembersAtom } from '@/hooks/use-sentinel-routing';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { humanize, sentBetween } from '@/lib/automation-replies';
import { AI_SCORE_PRESETS, EDIT_RATE_PRESETS, parseAiScore, parseEditRate, type DeviationReviewedFilter } from '@/lib/deviation-url';
import { cn } from '@/lib/utils';
import {
	FilterField,
	FiltersPopover,
	SETTINGS_INPUT_CLASS,
	SETTINGS_PILL_BUTTON_CLASS,
	SettingsMultiSelect,
	SettingsTableRows,
	type SettingsColumn,
	type SettingsMultiSelectOption
} from '@/modules/settings';
import type { Deviation } from '@/services/deviation.service';
import { AiScoreCell, SENT_VIA_LABELS, TicketLink, WritingIssueTags } from './deviation-cells';
import { DeviationDetails, sentByText } from './deviation-details';
import { deviationLogColumns, toDeviationFilter, type DeviationLogFilters } from './deviation-log-filters';

/**
 * Logs › Deviations: the selected brand's replies that failed the pre-send check and were sent anyway —
 * from the Sentinel workspace past a pause, or from the extension, checked after the helpdesk webhook.
 * A row opens the drawer: what the customer asked, the AI draft against what was sent, the sources, the
 * scores and the reviewer's verdict. Admins only, like the shadow report.
 */

const REVIEWED_LABELS: Record<DeviationReviewedFilter, string> = {
	yes: 'Reviewed',
	no: 'Not reviewed'
};

/** How a Min/Max pair reads, checks and names its values. */
interface RangeUnit {
	parse: (text: string) => number | undefined;
	invalid: string;
	inputMode: 'numeric' | 'decimal';
	placeholder: { min: string; max: string };
	ariaLabel: (bound: 'min' | 'max') => string;
	suffix?: string;
}

const EDIT_RATE_UNIT: RangeUnit = {
	parse: parseEditRate,
	invalid: 'Enter a whole percent from 0 to 100.',
	inputMode: 'numeric',
	placeholder: { min: '0', max: '100' },
	ariaLabel: (bound) => `Edit % ${bound} (percent)`,
	suffix: '%'
};

const AI_SCORE_UNIT: RangeUnit = {
	parse: parseAiScore,
	invalid: 'Enter a score from 0 to 5, with at most one decimal.',
	inputMode: 'decimal',
	placeholder: { min: '0', max: '5' },
	ariaLabel: (bound) => `AI score ${bound}`
};

/**
 * Min and Max as typed, applied when focus leaves the pair or on Enter, once both read as a valid
 * range. A range set elsewhere (a preset, Clear, a pasted link) replaces what is typed.
 */
function RangeInputs({
	unit,
	min,
	max,
	onApply
}: {
	unit: RangeUnit;
	min?: number;
	max?: number;
	onApply: (range: { min?: number; max?: number }) => void;
}) {
	const [minText, setMinText] = useState(min?.toString() ?? '');
	const [maxText, setMaxText] = useState(max?.toString() ?? '');
	const [error, setError] = useState<string | null>(null);
	// Follow the URL range during render (not in an effect), so applying our own range keeps focus where it is.
	const [shownRange, setShownRange] = useState({ min, max });
	if (shownRange.min !== min || shownRange.max !== max) {
		setShownRange({ min, max });
		setMinText(min?.toString() ?? '');
		setMaxText(max?.toString() ?? '');
		setError(null);
	}

	const apply = () => {
		const nextMin = minText.trim() ? unit.parse(minText) : undefined;
		const nextMax = maxText.trim() ? unit.parse(maxText) : undefined;
		if ((minText.trim() && nextMin == null) || (maxText.trim() && nextMax == null)) {
			setError(unit.invalid);
			return;
		}
		if (nextMin != null && nextMax != null && nextMin > nextMax) {
			setError('Min must not be above max.');
			return;
		}
		setError(null);
		if (nextMin !== min || nextMax !== max) onApply({ min: nextMin, max: nextMax });
	};

	const input = (label: 'Min' | 'Max', value: string, onChange: (text: string) => void) => (
		<label className='flex flex-1 flex-col gap-1 text-[11px] text-gray-800'>
			{label}
			<span className='relative'>
				<input
					type='text'
					inputMode={unit.inputMode}
					value={value}
					placeholder={label === 'Min' ? unit.placeholder.min : unit.placeholder.max}
					aria-label={unit.ariaLabel(label === 'Min' ? 'min' : 'max')}
					aria-invalid={error ? true : undefined}
					onChange={(e) => {
						onChange(e.target.value);
						setError(null);
					}}
					onKeyDown={(e) => e.key === 'Enter' && apply()}
					className={cn(SETTINGS_INPUT_CLASS, 'h-9 tabular-nums', unit.suffix && 'pr-7', error && 'border-red-500')}
				/>
				{unit.suffix && (
					<span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-gray-800'>{unit.suffix}</span>
				)}
			</span>
		</label>
	);

	return (
		<div
			className='flex flex-col gap-1'
			onBlur={(e) => {
				// Moving between Min and Max is still typing; leaving both applies.
				if (!e.currentTarget.contains(e.relatedTarget as Node | null)) apply();
			}}
		>
			<div className='flex gap-2'>
				{input('Min', minText, setMinText)}
				{input('Max', maxText, setMaxText)}
			</div>
			{error && (
				<p role='alert' className='text-[12px] text-red-600'>
					{error}
				</p>
			)}
		</div>
	);
}

/** The Edit % range: presets (a minimum, no maximum), Min and Max, and Clear. */
function EditRateRangeField({ filters }: { filters: DeviationLogFilters }) {
	const { editRateMin: min, editRateMax: max, setEditRateRange } = filters;
	return (
		<FilterField label='Edit %'>
			<div className='flex flex-wrap items-center gap-1.5'>
				{EDIT_RATE_PRESETS.map((preset) => {
					const active = min === preset && max == null;
					return (
						<button
							key={preset}
							type='button'
							aria-pressed={active}
							onClick={() => setEditRateRange(active ? {} : { min: preset })}
							className={cn(SETTINGS_PILL_BUTTON_CLASS, 'h-7 px-2.5 tabular-nums', active && 'border-ink bg-ink/8')}
						>
							≥{preset}%
						</button>
					);
				})}
				{(min != null || max != null) && (
					<button
						type='button'
						onClick={() => setEditRateRange({})}
						className='ml-auto text-[12px] text-gray-900 underline-offset-2 hover:underline'
					>
						Clear
					</button>
				)}
			</div>
			<RangeInputs unit={EDIT_RATE_UNIT} min={min} max={max} onApply={setEditRateRange} />
		</FilterField>
	);
}

/**
 * The AI score: Below threshold (the score itself paused the reply, under that check's own threshold),
 * presets (a maximum, no minimum), Min and Max, and Clear. Below threshold combines with a range.
 */
function AiScoreRangeField({ filters }: { filters: DeviationLogFilters }) {
	const { aiScoreMin: min, aiScoreMax: max, belowThreshold, setAiScoreRange, setBelowThreshold, clearAiScore } = filters;
	const pill = (active: boolean) => cn(SETTINGS_PILL_BUTTON_CLASS, 'h-7 px-2.5 tabular-nums', active && 'border-ink bg-ink/8');
	return (
		<FilterField label='AI score'>
			<div className='flex flex-wrap items-center gap-1.5'>
				<button
					type='button'
					aria-pressed={belowThreshold}
					onClick={() => setBelowThreshold(!belowThreshold)}
					className={pill(belowThreshold)}
				>
					Below threshold
				</button>
				{AI_SCORE_PRESETS.map((preset) => {
					const active = max === preset && min == null;
					return (
						<button
							key={preset}
							type='button'
							aria-pressed={active}
							onClick={() => setAiScoreRange(active ? {} : { max: preset })}
							className={pill(active)}
						>
							≤ {preset}
						</button>
					);
				})}
				{(min != null || max != null || belowThreshold) && (
					<button type='button' onClick={clearAiScore} className='ml-auto text-[12px] text-gray-900 underline-offset-2 hover:underline'>
						Clear
					</button>
				)}
			</div>
			<RangeInputs unit={AI_SCORE_UNIT} min={min} max={max} onApply={setAiScoreRange} />
		</FilterField>
	);
}

/** Values from a shared link stay selectable even when this window's options do not name them. */
const withLinkedOptions = (options: SettingsMultiSelectOption[], picked: string[]): SettingsMultiSelectOption[] => {
	const missing = picked.filter((value) => !options.some((option) => option.value.toLowerCase() === value.toLowerCase()));
	return [...missing.map((value) => ({ value, label: value, text: value })), ...options];
};

/**
 * The Sent by and Intent filters, several values each. The options are the selected brand's, for the
 * chosen dates; this renders inside the Filters popover, so they are asked for only while it is open.
 */
function DeviationOptionFields({ filters }: { filters: DeviationLogFilters }) {
	const { senders, intents } = useDeviationFilterOptions(sentBetween(filters.days));
	const senderOptions = withLinkedOptions(
		senders.map((sender) => ({
			value: sender.email,
			text: sender.email,
			label: (
				<span className='flex min-w-0 items-center gap-1.5'>
					<span className='truncate'>{sender.email}</span>
					<span className='shrink-0 text-[11px] text-gray-800'>· {SENT_VIA_LABELS[sender.via]}</span>
				</span>
			)
		})),
		filters.senders
	);
	const intentOptions = withLinkedOptions(
		intents.map((name) => ({
			value: name,
			text: humanize(name),
			label: <span className='capitalize'>{humanize(name)}</span>
		})),
		filters.intents
	);
	return (
		<>
			<FilterField label='Sent by'>
				<SettingsMultiSelect
					values={filters.senders}
					onValuesChange={filters.setSenders}
					options={senderOptions}
					allLabel='All senders'
					aria-label='Sent by'
				/>
			</FilterField>
			<FilterField label='Intent'>
				<SettingsMultiSelect
					values={filters.intents}
					onValuesChange={filters.setIntents}
					options={intentOptions}
					allLabel='All intents'
					aria-label='Intent'
					className='capitalize'
				/>
			</FilterField>
		</>
	);
}

/** One choice of several, or All; a second click on the picked one goes back to All. */
function ChoiceField<Value extends string>({
	label,
	value,
	labels,
	onChange
}: {
	label: string;
	value: Value | undefined;
	labels: Record<Value, string>;
	onChange: (value: Value | undefined) => void;
}) {
	const choices = Object.keys(labels) as Value[];
	const pill = (active: boolean) => cn(SETTINGS_PILL_BUTTON_CLASS, 'h-7 px-2.5', active && 'border-ink bg-ink/8');
	return (
		<FilterField label={label}>
			<div className='flex flex-wrap items-center gap-1.5' role='group' aria-label={label}>
				<button type='button' aria-pressed={value === undefined} onClick={() => onChange(undefined)} className={pill(value === undefined)}>
					All
				</button>
				{choices.map((choice) => (
					<button
						key={choice}
						type='button'
						aria-pressed={value === choice}
						onClick={() => onChange(value === choice ? undefined : choice)}
						className={pill(value === choice)}
					>
						{labels[choice]}
					</button>
				))}
			</div>
		</FilterField>
	);
}

/**
 * The sent-between dates, opened like the Conversations (Shadow AI) picker: below its trigger with the
 * page's inset, not flush over the tabs. Any date clears them.
 */
function DeviationDateRange({ filters }: { filters: DeviationLogFilters }) {
	return (
		<div className='flex items-center gap-1'>
			<DateRangePicker
				value={filters.days}
				onChange={filters.setDays}
				ariaLabel='Sent between'
				placeholder={{ start: 'Start date', end: 'End date' }}
				className='h-8'
			/>
			{filters.days.startDate && (
				<Button variant='ghost' size='icon-sm' className='rounded-full' aria-label='Any date' onClick={() => filters.setDays({})}>
					<X className='size-3.5' />
				</Button>
			)}
		</div>
	);
}

/** Search, dates and Filters for the page header. */
export function DeviationLogTools({ filters }: { filters: DeviationLogFilters }) {
	const activeCount = [
		filters.sentVia !== undefined,
		filters.reviewed !== undefined,
		filters.intents.length > 0,
		filters.senders.length > 0,
		filters.editRateMin != null || filters.editRateMax != null,
		filters.aiScoreMin != null || filters.aiScoreMax != null || filters.belowThreshold
	].filter(Boolean).length;
	return (
		<>
			<SearchInput
				value={filters.query}
				onChange={(e) => filters.setQuery(e.target.value)}
				placeholder='Search ticket ID'
				aria-label='Search deviations by ticket ID'
				className='h-8 w-[200px] border-gray-500 bg-white'
			/>
			<DeviationDateRange filters={filters} />
			<FiltersPopover activeCount={activeCount}>
				<ChoiceField label='Sent via' value={filters.sentVia} labels={SENT_VIA_LABELS} onChange={filters.setSentVia} />
				<ChoiceField label='Reviewed' value={filters.reviewed} labels={REVIEWED_LABELS} onChange={filters.setReviewed} />
				<DeviationOptionFields filters={filters} />
				<AiScoreRangeField filters={filters} />
				<EditRateRangeField filters={filters} />
			</FiltersPopover>
		</>
	);
}

/**
 * Above the Deviations table while the selected brand's pre-send quality gate is off: new replies are not
 * checked, so no new deviations can show up. Administrators, who own the switch, get a link straight to
 * it; anyone else is told who can turn it on. Nothing shows while the setting loads or if it fails to.
 */
export function DeviationGateNotice() {
	const { can } = usePermissions();
	const gate = useSendGateConfig(can('view', 'sentinel-workspace'));
	const canTurnOn = useAtomValue(canManageSentinelMembersAtom);
	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const { businessUnits } = useBusinessUnits();
	if (gate?.enabled !== false) return null;

	const brandName = businessUnits.find((unit) => unit.id === selectedBusinessUnitId)?.name ?? 'this brand';
	return (
		<div className='px-4 pt-3'>
			<div role='status' className='flex items-center gap-3 rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2.5'>
				<ShieldOff className='size-4 shrink-0 text-amber-700' aria-hidden='true' />
				<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
					<p className='text-[13px] font-medium leading-[18px] text-amber-800'>Pre-send quality gate is off for {brandName}</p>
					<p className='text-[12px] leading-4 text-amber-700'>
						Replies aren&apos;t checked before they&apos;re sent, so no new deviations show up here.
						{!canTurnOn && ' Ask an administrator to turn it on.'}
					</p>
				</div>
				{canTurnOn && (
					<Link to={SEND_GATE_SETTINGS_URL} className={cn(SETTINGS_PILL_BUTTON_CLASS, 'h-8 text-[12px] font-medium')}>
						Turn on in settings
						<ArrowRight className='size-3.5' aria-hidden='true' />
					</Link>
				)}
			</div>
		</div>
	);
}

function EditRateBar({ editRate }: { editRate: number | null }) {
	if (editRate == null) return <span className='text-gray-700'>—</span>;
	return (
		<span className='flex w-full min-w-0 items-center gap-2'>
			<span className='h-1.5 flex-1 overflow-hidden rounded-full bg-gray-300' aria-hidden='true'>
				<span className='block h-full rounded-full bg-amber-500' style={{ width: `${editRate}%` }} />
			</span>
			<span className='w-9 shrink-0 text-right tabular-nums'>{editRate}%</span>
		</span>
	);
}

function SentByCell({ row }: { row: Deviation }) {
	return (
		<span className='flex min-w-0 flex-col'>
			<span className='truncate' title={row.sentBy ?? undefined}>
				{row.sentBy ?? <span className='text-gray-700'>Unknown sender</span>}
			</span>
			<span className='text-[11px] leading-4 text-gray-800'>{SENT_VIA_LABELS[row.sentVia]}</span>
		</span>
	);
}

const DEVIATION_LOG_CELLS: Record<string, (row: Deviation) => ReactNode> = {
	time: (row) => <span className='truncate tabular-nums'>{format(new Date(row.sentAt), 'd MMM yyyy, HH:mm')}</span>,
	ticket: (row) => <TicketLink ticketId={row.ticketId} ticketUrl={row.ticketUrl} />,
	intent: (row) =>
		row.intent ? <span className='truncate capitalize'>{humanize(row.intent)}</span> : <span className='text-gray-700'>—</span>,
	sentBy: (row) => <SentByCell row={row} />,
	aiScore: (row) => <AiScoreCell aiScore={row.aiScore} blockBelow={row.blockBelow} />,
	writingIssues: (row) => <WritingIssueTags issues={row.writingIssues} />,
	editRate: (row) => <EditRateBar editRate={row.editRate} />
};

export function DeviationLog({ filters }: { filters: DeviationLogFilters }) {
	const [openId, setOpenId] = useState<string | null>(null);
	const {
		searchedTicket,
		days,
		sentVia,
		reviewed,
		intents,
		senders,
		editRateMin,
		editRateMax,
		aiScoreMin,
		aiScoreMax,
		belowThreshold,
		sort,
		setSort
	} = filters;
	const { from, to } = sentBetween(days);
	// Memoised on the values: the deviations hook makes a new query whenever this object changes.
	const filter = useMemo(
		() =>
			toDeviationFilter({
				searchedTicket,
				days,
				sentVia,
				reviewed,
				intents,
				senders,
				editRateMin,
				editRateMax,
				aiScoreMin,
				aiScoreMax,
				belowThreshold,
				sort
			}),
		[searchedTicket, from, to, sentVia, reviewed, intents, senders, editRateMin, editRateMax, aiScoreMin, aiScoreMax, belowThreshold, sort] // eslint-disable-line react-hooks/exhaustive-deps -- days by value
	);
	const filtered = Boolean(
		filter.q ||
		filter.from ||
		filter.sentVia ||
		filter.reviewed ||
		filter.intent ||
		filter.sender ||
		filter.editRateMin != null ||
		filter.editRateMax != null ||
		filter.aiScoreMin != null ||
		filter.aiScoreMax != null ||
		filter.belowThreshold
	);
	const { entries, isLoading, isError, refetch, hasMore, loadingMore, loadMore } = useDeviations(filter);
	const columns: SettingsColumn<Deviation>[] = deviationLogColumns(sort, setSort).map((head) => ({
		...head,
		cell: DEVIATION_LOG_CELLS[head.id]
	}));
	const open = entries.find((entry) => entry.checkId === openId);

	return (
		<>
			{isError ? (
				<div className='flex flex-col items-center gap-3 py-12'>
					<p className='text-[13px] text-gray-900'>Deviations could not be loaded.</p>
					<Button variant='outline' size='sm' onClick={() => void refetch()}>
						Retry
					</Button>
				</div>
			) : (
				<SettingsTableRows
					columns={columns}
					rows={entries}
					rowKey={(row) => row.checkId}
					isLoading={isLoading}
					onRowClick={(row) => setOpenId(row.checkId)}
					empty={
						<div className='flex flex-col items-center gap-3'>
							<span className='flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50'>
								<GitCompareArrows className='h-4 w-4 text-indigo-600' />
							</span>
							<p className='text-[13px] font-medium text-ink'>{filtered ? 'Nothing matches' : 'No deviations yet'}</p>
							<p className='max-w-[420px] text-[12px] leading-4 text-gray-900'>
								{filtered
									? 'Try other dates, filters or another ticket ID.'
									: 'Replies that fail the pre-send check and are sent anyway show up here.'}
							</p>
						</div>
					}
				/>
			)}
			{hasMore && (
				<div className='flex justify-center py-4'>
					<Button variant='outline' size='sm' onClick={loadMore} disabled={loadingMore} className='rounded-full'>
						{loadingMore ? 'Loading…' : 'Load more'}
					</Button>
				</div>
			)}

			<Sheet open={open != null} onOpenChange={(isOpen) => !isOpen && setOpenId(null)}>
				{/* Solid white like the select popover; the header stays put while the body scrolls. */}
				<SheetContent className='w-[760px] gap-0 bg-white p-0 backdrop-blur-none sm:max-w-[760px]'>
					<SheetHeader className='shrink-0 gap-1 border-b border-gray-300 px-5 py-4 pr-12'>
						<SheetTitle className='text-[15px]'>
							{open ? <TicketLink ticketId={open.ticketId} ticketUrl={open.ticketUrl} prefix='Ticket ' /> : 'Deviation'}
						</SheetTitle>
						<SheetDescription className='text-[12px]'>
							{open
								? `${sentByText(open)} · ${format(new Date(open.sentAt), 'd MMM yyyy, HH:mm')}`
								: 'This reply is no longer in the list.'}
						</SheetDescription>
					</SheetHeader>
					<div className='min-h-0 flex-1 overflow-y-auto'>{open && <DeviationDetails key={open.checkId} deviation={open} />}</div>
				</SheetContent>
			</Sheet>
		</>
	);
}
